import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  FIRE_SIGHT_SIMULATION_BASE_PRODUCT_CODES,
  FIRE_SIGHT_SIMULATION_LAYER_DEFINITIONS,
} from '../../constants/fire-sight.constants';
import {
  IFireSightAsset,
  IFireSightLayerDefinition,
  IFireSightObservation,
} from '../../interfaces/fire-sight/IFireSightModels';

@Injectable({ providedIn: 'root' })
export class FireSightAssetResolverService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.services.urlEDI;
  private readonly presignedUrlCache = new Map<string, Observable<string | null>>();

  getSimulationLayerDefinitions(): readonly IFireSightLayerDefinition[] {
    return FIRE_SIGHT_SIMULATION_LAYER_DEFINITIONS;
  }

  findAsset(
    assets: IFireSightAsset[] | null | undefined,
    productCode: string,
  ): IFireSightAsset | null {
    return assets?.find((asset) => asset.product_code === productCode) ?? null;
  }

  resolveSimulationBaseAsset(
    observation: IFireSightObservation | null | undefined,
  ): IFireSightAsset | null {
    if (!observation) {
      return null;
    }

    for (const productCode of FIRE_SIGHT_SIMULATION_BASE_PRODUCT_CODES) {
      const asset = this.findAsset(observation.assets, productCode);
      if (asset) {
        return asset;
      }
    }

    return null;
  }

  resolveAssetUrl(asset: IFireSightAsset): Observable<string | null> {
    if (!asset.s3_uri.startsWith('s3://')) {
      return of(asset.s3_uri);
    }

    const cached = this.presignedUrlCache.get(asset.s3_uri);
    if (cached) {
      return cached;
    }

    const request$ = this.http
      .post<{ urls: Array<string | null> }>(`${this.apiUrl}/layer/presign`, {
        uris: [asset.s3_uri],
      })
      .pipe(
        map((response) => response.urls[0] ?? null),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.presignedUrlCache.set(asset.s3_uri, request$);
    return request$;
  }

  clearPresignedUrlCache(): void {
    this.presignedUrlCache.clear();
  }
}
